import adminApi from './adminAuth';

export const adminCarService = {
  getAllCars: async (params = {}) => {
    const response = await adminApi.get('/admin/cars', { params });
    return response.data;
  },

  getCarDetails: async (carId) => {
    const response = await adminApi.get(`/admin/cars/${carId}`);
    return response.data;
  },


  // ── createCar: handles images + interior_video ──────────────────────────
  createCar: async (carData) => {
    const formData = new FormData();

    // Append all scalar fields except file arrays
    const fileKeys = ['images', 'exterior_images', 'interior_images', 'interior_video'];
    Object.keys(carData).forEach(key => {
      if (!fileKeys.includes(key) && carData[key] !== null && carData[key] !== undefined) {
        formData.append(key, carData[key]);
      }
    });

    // Image arrays
    if (carData.images?.length > 0)
      carData.images.forEach(img => formData.append('images[]', img));
    if (carData.exterior_images?.length > 0)
      carData.exterior_images.forEach(img => formData.append('exterior_images[]', img));
    if (carData.interior_images?.length > 0)
      carData.interior_images.forEach(img => formData.append('interior_images[]', img));

    // Single interior video file
    if (carData.interior_video instanceof File) {
      formData.append('interior_video', carData.interior_video);
    }

    const response = await adminApi.post('/admin/cars', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  // ── updateCar: handles images + interior_video ──────────────────────────
  updateCar: async (carId, carData) => {
    const formData = new FormData();

    const fileKeys = ['images', 'exterior_images', 'interior_images', 'interior_video'];
    Object.keys(carData).forEach(key => {
      if (!fileKeys.includes(key) && carData[key] !== null && carData[key] !== undefined) {
        formData.append(key, carData[key]);
      }
    });

    if (carData.images?.length > 0)
      carData.images.forEach(img => { if (img instanceof File) formData.append('images[]', img); });
    if (carData.exterior_images?.length > 0)
      carData.exterior_images.forEach(img => { if (img instanceof File) formData.append('exterior_images[]', img); });
    if (carData.interior_images?.length > 0)
      carData.interior_images.forEach(img => { if (img instanceof File) formData.append('interior_images[]', img); });

    if (carData.interior_video instanceof File) {
      formData.append('interior_video', carData.interior_video);
    }

    const response = await adminApi.put(`/admin/cars/${carId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  deleteAttachment: async (carId, attachmentType, attachmentId = null) => {
    let url;
    if (attachmentType === 'interior_video') {
      // Video has no attachment id — use a placeholder segment so route matches
      url = `/admin/cars/${carId}/attachments/video?attachment_type=interior_video`;
    } else {
      url = `/admin/cars/${carId}/attachments/${attachmentId}?attachment_type=${attachmentType}`;
    }
    const response = await adminApi.delete(url);
    return response.data;
  },

  deleteCar:  async (carId) => (await adminApi.delete(`/admin/cars/${carId}`)).data,
  approveCar: async (carId) => (await adminApi.put(`/admin/cars/${carId}/approve`)).data,
  rejectCar:  async (carId) => (await adminApi.put(`/admin/cars/${carId}/reject`)).data,
  markSold:   async (carId) => (await adminApi.put(`/admin/cars/${carId}/mark_sold`)).data,
  reserveCar: async (carId) => (await adminApi.put(`/admin/cars/${carId}/reserve`)).data,
  getStats:   async ()      => (await adminApi.get('/admin/cars/stats')).data,
};



// ==================== CAR FEATURES ====================
export const adminCarFeaturesService = {
  getFeatures:        async (carId)                  => (await adminApi.get(`/admin/cars/${carId}/car_features`)).data,
  createFeature:      async (carId, data)            => (await adminApi.post(`/admin/cars/${carId}/car_features`, data)).data,
  bulkCreateFeatures: async (carId, features)        => (await adminApi.post(`/admin/cars/${carId}/car_features/bulk_create`, { features })).data,
  updateFeature:      async (carId, featureId, data) => (await adminApi.put(`/admin/cars/${carId}/car_features/${featureId}`, data)).data,
  deleteFeature:      async (carId, featureId)       => (await adminApi.delete(`/admin/cars/${carId}/car_features/${featureId}`)).data,
};

// ==================== CAR BENEFITS ====================
export const adminCarBenefitsService = {
  getBenefits:        async (carId)                  => (await adminApi.get(`/admin/cars/${carId}/car_benefits`)).data,
  createBenefit:      async (carId, data)            => (await adminApi.post(`/admin/cars/${carId}/car_benefits`, data)).data,
  bulkCreateBenefits: async (carId, benefits)        => (await adminApi.post(`/admin/cars/${carId}/car_benefits/bulk_create`, { benefits })).data,
  updateBenefit:      async (carId, benefitId, data) => (await adminApi.put(`/admin/cars/${carId}/car_benefits/${benefitId}`, data)).data,
  deleteBenefit:      async (carId, benefitId)       => (await adminApi.delete(`/admin/cars/${carId}/car_benefits/${benefitId}`)).data,
};

// ==================== CAR SPECIFICATIONS ====================
export const adminCarSpecificationsService = {
  getSpecification:    async (carId)       => (await adminApi.get(`/admin/cars/${carId}/car_specification`)).data,
  upsertSpecification: async (carId, data) => (await adminApi.post(`/admin/cars/${carId}/car_specification/upsert`, data)).data,
};

export const adminCarContentService = {
  verifyContent: async (carId) => (await adminApi.put(`/admin/cars/${carId}/content/verify`)).data,
};

export const adminCarPublishService = {
  publishCar: async (carId) => (await adminApi.put(`/admin/cars/${carId}/publish`)).data,
};

export const adminCarCreationService = {
  createFromInspection: async (inspectionId) => {
    try {
      const response = await adminApi.post(`/admin/car_inspections/${inspectionId}/car_creation`);
      return response.data;
    } catch (error) {
      const msg = error.response?.data?.error || error.response?.data?.message || error.message;
      throw new Error(msg);
    }
  },
};

export const adminInventoryService = {
  moveToInventory: async (carId) => (await adminApi.post(`/admin/inventory/${carId}`)).data,
};

// ==================== USERS ====================
export const adminUserService = {
  getAllUsers:     async (params = {}) => (await adminApi.get('/admin/users', { params })).data,
  getUserDetails: async (userId)       => (await adminApi.get(`/admin/users/${userId}`)).data,
  activateUser:   async (userId)       => (await adminApi.put(`/admin/users/${userId}/activate`)).data,
  deactivateUser: async (userId)       => (await adminApi.put(`/admin/users/${userId}/deactivate`)).data,
  deleteUser:     async (userId)       => (await adminApi.delete(`/admin/users/${userId}`)).data,
   createUser: async (data) => (await adminApi.post('/admin/users', data)).data,
  updateUser: async (userId, data) => (await adminApi.put(`/admin/users/${userId}`, data)).data,

};

// ==================== SELL REQUESTS ====================
export const adminSellRequestService = {
  getAllSellRequests: () => adminApi.get('/admin/sell_requests'),
  getSellRequest:    (id) => adminApi.get(`/admin/sell_requests/${id}`),
  approveSellRequest:(id) => adminApi.post(`/admin/sell_requests/${id}/approve`),
  rejectSellRequest: (id) => adminApi.post(`/admin/sell_requests/${id}/reject`),
  updateStatus:      (id, data) => adminApi.patch(`/admin/sell_requests/${id}/update_status`, data),
  assignInspector:   (id, inspectorId) =>
    adminApi.post(`/admin/sell_requests/${id}/assign_inspector`, { inspector_id: inspectorId }),
};
// ==================== INSPECTIONS ====================
export const adminInspectionService = {
  getAllInspections:       async ()      => (await adminApi.get('/admin/car_inspections')).data,
  getWorkflowInspections: async ()      => (await adminApi.get('/admin/car_inspections/workflow')).data,
  getInspection:          async (id)    => (await adminApi.get(`/admin/car_inspections/${id}`)).data,
  getInspectionByCar:     async (carId) => (await adminApi.get(`/admin/car_inspections/by_car/${carId}`)).data,
  assignInspector: async (id, inspectorId) =>
    (await adminApi.put(`/admin/car_inspections/${id}/assign_inspector`, { car_inspection: { inspector_id: inspectorId } })).data,
  approveInspection: async (id, data) =>
    (await adminApi.put(`/admin/car_inspections/${id}/approve`, { car_inspection: data })).data,
  rejectInspection: async (id) =>
    (await adminApi.put(`/admin/car_inspections/${id}/reject`)).data,
      // Add inside adminInspectionService object:
  downloadPdf: async (id) => {
    const response = await adminApi.get(
      `/admin/car_inspections/${id}/download_pdf`,
      { responseType: 'blob' }
    );
    const url  = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href  = url;
    link.setAttribute('download', `inspection_report_${id}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },
};



export const adminInspectorService = {
  getAllInspectors: async () => (await adminApi.get('/admin/inspectors')).data,
};

export const adminPricingService = {
  createPricing: async (inspectionId, pricingData) =>
    (await adminApi.post(`/admin/car_inspections/${inspectionId}/pricing`, pricingData)).data,
};

// ==================== PAYMENTS ====================
export const adminPaymentService = {
  getAllPayments: async () => {
    const response = await adminApi.get('/admin/payments');
    const body = response.data;
    if (Array.isArray(body))       return body;
    if (Array.isArray(body?.data)) return body.data;
    return [];
  },
  getPaymentDetails: async (paymentId) => {
    const response = await adminApi.get(`/admin/payments/${paymentId}`);
    return response.data?.data || response.data;
  },
  getPaymentStats: async () => {
    const response = await adminApi.get('/admin/payments/stats');
    return response.data?.data || response.data;
  },
  refundPayment: async (paymentId) =>
    (await adminApi.post(`/admin/payments/${paymentId}/refund`)).data,
};

// ==================== BOOKINGS ====================
export const adminBookingService = {
  getAllBookings: async (params = {}) => {
    const response = await adminApi.get('/admin/bookings', { params });
    const body = response.data;
    return Array.isArray(body) ? body : (body?.bookings || []);
  },
  getBooking: async (bookingId) => {
    const response = await adminApi.get(`/admin/bookings/${bookingId}`);
    return response.data?.booking || response.data;
  },
  confirmBooking: async (bookingId) => (await adminApi.put(`/admin/bookings/${bookingId}/confirm`)).data,
  cancelBooking:  async (bookingId, reason = null) =>
    (await adminApi.post(`/admin/bookings/${bookingId}/cancel`, { reason })).data,
};



// ==================== LOGISTICS ====================
export const adminLogisticsService = {
  getPickupRequests: async (city) => {
    const params = city ? { city } : {};
    const response = await adminApi.get('/admin/pickup_requests', { params });
    const body = response.data;
    return Array.isArray(body) ? body : (body?.pickup_requests || []);
  },
  updatePickupRequest: async (id, data) => {
    const response = await adminApi.put(`/admin/pickup_requests/${id}`, { pickup_request: data });
    return response.data;
  },
  getDeliveryRequests: async (city) => {
    const params = city ? { city } : {};
    const response = await adminApi.get('/admin/delivery_requests', { params });
    const body = response.data;
    return Array.isArray(body) ? body : (body?.delivery_requests || []);
  },
  updateDeliveryRequest: async (id, data) => {
    const response = await adminApi.put(`/admin/delivery_requests/${id}`, { delivery_request: data });
    return response.data;
  },
  getDrivers: async () => {
    try {
      const response = await adminApi.get('/admin/users', { params: { role: 'driver' } });
      const body = response.data;
      let list = [];
      if (Array.isArray(body))             list = body;
      else if (Array.isArray(body?.users)) list = body.users;
      else if (Array.isArray(body?.data))  list = body.data;
      return list.filter(u => u.role === 'driver');
    } catch (err) {
      console.warn('getDrivers failed:', err.message);
      return [];
    }
  },
};

export const adminTestDriveService = {
  getAllTestDrives: async (params = {}) => {
    const response = await adminApi.get('/admin/test_drives', { params });
    return response.data; // { test_drives, meta, stats }
  },
 
  getTestDrive: async (id) => {
    const response = await adminApi.get(`/admin/test_drives/${id}`);
    return response.data;
  },
 
  updateStatus: async (id, status) => {
    const response = await adminApi.put(`/admin/test_drives/${id}/update_status`, { status });
    return response.data;
  },
 
  deleteTestDrive: async (id) => {
    const response = await adminApi.delete(`/admin/test_drives/${id}`);
    return response.data;
  },
};
 
 // ==================== DEALER MANAGEMENT ====================
// Add this to your existing src/services/adminService.js
export const adminDealerService = {
  // Notify dealers about a car inspection with price range
  notifyDealers: async (inspectionId, priceData) => {
    const response = await adminApi.post(
      `/admin/car_inspections/${inspectionId}/notify_dealers`,
      priceData
    );
    return response.data;
  },

  // Get all offers for a specific dealer listing
  getDealerOffers: async (dealerListingId) => {
    const response = await adminApi.get(
      `/admin/dealer_listings/${dealerListingId}/dealer_offers`
    );
    return response.data;
  },

  // Accept a specific dealer offer
  acceptOffer: async (offerId) => {
    const response = await adminApi.put(`/admin/dealer_offers/${offerId}/accept`);
    return response.data;
  },

  // Reject a specific dealer offer
  rejectOffer: async (offerId) => {
    const response = await adminApi.put(`/admin/dealer_offers/${offerId}/reject`);
    return response.data;
  },

  // Get all dealer listings (for the Dealer Listings tab)
  getAllDealerListings: async () => {
    const response = await adminApi.get('/admin/dealer_listings');
    return response.data;
  },
};